import React, { createContext, useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import customParseFormat from 'dayjs/plugin/customParseFormat';

import type { OrganizationSearchParams } from './use-organization-planner-search-params';
import useOrganizationPlannerSearchParams, {
  organizationSearchParamsInitialValue,
} from './use-organization-planner-search-params';
import usePlannerSearchParams from './use-planner-search-params';
import type { TablePlannerParams } from './use-table-planner-params';
import useTablePlannerParams, {
  tablePlannerParamsInitialValue,
} from './use-table-planner-params';
import type { WeekPlannerParams } from './use-week-planner-params';
import useWeekPlannerParams, {
  weekPlannerParamsInitialValue,
} from './use-week-planner-params';

dayjs.extend(customParseFormat);

type OrganizationWeekPlannerContext = {
  gridParams: WeekPlannerParams;
  tableParams: TablePlannerParams;
  organizationPlannerSearchParams: OrganizationSearchParams;
};

const organizationWeekPlannerContextInitialValue: OrganizationWeekPlannerContext =
  {
    gridParams: weekPlannerParamsInitialValue,
    organizationPlannerSearchParams: organizationSearchParamsInitialValue,
    tableParams: tablePlannerParamsInitialValue,
  };

const OrganizationWeekPlannerCtx =
  createContext<OrganizationWeekPlannerContext>(
    organizationWeekPlannerContextInitialValue,
  );

const OrganizationWeekPlannerContextProvider: React.FC<{
  children: JSX.Element;
}> = (props) => {
  const plannerSearchParams = usePlannerSearchParams();
  const organizationPlannerSearchParams =
    useOrganizationPlannerSearchParams(plannerSearchParams);
  const gridParams = useWeekPlannerParams(
    organizationPlannerSearchParams,
    plannerSearchParams,
  );
  const tableParams = useTablePlannerParams(
    organizationPlannerSearchParams,
    plannerSearchParams,
  );

  const organizationWeekPlannerContext =
    useMemo((): OrganizationWeekPlannerContext => {
      return {
        gridParams,
        organizationPlannerSearchParams,
        tableParams,
      };
    }, [gridParams, organizationPlannerSearchParams, tableParams]);

  return (
    <OrganizationWeekPlannerCtx.Provider value={organizationWeekPlannerContext}>
      {props.children}
    </OrganizationWeekPlannerCtx.Provider>
  );
};

export const useOrganizationWeekPlanner = (): OrganizationWeekPlannerContext =>
  useContext(OrganizationWeekPlannerCtx);

export default {
  Consumer: OrganizationWeekPlannerCtx.Consumer,
  Provider: OrganizationWeekPlannerContextProvider,
};
